import React from "react";
import {
  MenuItem,
  IconButton,
  Paper,
  ClickAwayListener,
  MenuList,
  Popper,
  Grow,
} from "@mui/material";
import { Link, useHistory } from "react-router-dom";
import { createUseStyles } from "react-jss";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Settings4LineIcon from "remixicon-react/Settings4LineIcon";
import { Text } from "$components";
import useAuth from "$hooks/useAuth";
import { useAppReset } from "$hooks/useAppReset";
import { publicIconPath } from "$utils/constants";
import { theme } from "$utils/theme/bataillog";

const useStyles = createUseStyles(() => ({
  root: {
    flexGrow: 1,
  },
  menuButton: {
    minWidth: 173,
    marginTop: "5px !important",
  },
  title: {
    flexGrow: 1,
  },
  Link: {
    color: "black",
    textDecoration: "unset",
  },
  iconImg: {
    height: 25,
    color: "white",
  },
  iconImgLogout: {
    height: 15,
    marginRight: theme.spacing(2),
  },
  IconButton: {
    "&.MuiButtonBase-root": {
      backgroundColor: theme.color.blueLight,
      padding: 9,
      "&:hover": {
        backgroundColor: theme.color.blueLighter,
      },
    },
  },
  ExpandMoreIcon: {
    "&.MuiSvgIcon-root": {
      cursor: "pointer",
      color: theme.color.gray5,
      marginLeft: 10,
    },
  },
  iconContainer: {
    display: "flex",
    alignItems: "center",
    marginLeft: theme.spacing(2),
  },
  verticalSeparator: {
    height: 1,
    width: "100%",
    background: theme.color.gray1,
    margin: [5, 0],
  },
}));

export default function ButtonAppBar() {
  const classes = useStyles();
  const { resetAll } = useAppReset();
  const [open, setOpen] = React.useState(false);
  const { logout } = useAuth();
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const { push } = useHistory();
  const { isAdmin } = useAuth();

  function handleListKeyDown(event: React.KeyboardEvent) {
    if (event.key === "Tab") {
      event.preventDefault();
      setOpen(false);
    }
  }

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (
      // eslint-disable-next-line operator-linebreak
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setOpen(false);
  };

  const handleLogout = () => {
    resetAll().then(() => {
      logout();
      push("/connexion");
    });
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = React.useRef(open);
  React.useEffect(() => {
    if (prevOpen.current === true && open === false) {
      if (anchorRef.current) {
        anchorRef.current.focus();
      }
    }

    prevOpen.current = open;
  }, [open]);

  const handleMenuItemClose = () => {
    setOpen(false);
  };

  return (
    <span>
      <div
        className={classes.iconContainer}
        aria-controls={open ? "menu-list-grow" : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
        onKeyPress={handleToggle}
        role="button"
        tabIndex={0}
        ref={anchorRef}
      >
        <IconButton
          aria-label="account of current user"
          aria-controls="menu-appbar"
          aria-haspopup="true"
          className={classes.IconButton}
        >
          <Settings4LineIcon className={classes.iconImg} />
        </IconButton>
        <ExpandMoreIcon className={classes.ExpandMoreIcon} />
      </div>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        placement="bottom-end"
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === "bottom" ? "center top" : "center bottom",
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  autoFocusItem={open}
                  id="menu-list-grow"
                  onKeyDown={handleListKeyDown}
                  className={classes.menuButton}
                >
                  {[
                    <Link key="profil" to="/profil" className={classes.Link}>
                      <MenuItem onClick={handleMenuItemClose}>
                        <Text>Mon profil</Text>
                      </MenuItem>
                    </Link>,
                    isAdmin() && (
                      <span key="conf">
                        <div className={classes.verticalSeparator} />
                        <Link to="/configurations" className={classes.Link}>
                          <MenuItem onClick={handleMenuItemClose}>
                            <Text>Configurations</Text>
                          </MenuItem>
                        </Link>
                        <div className={classes.verticalSeparator} />
                      </span>
                    ),
                  ]}
                  {/* eslint-disable-next-line jsx-a11y/interactive-supports-focus */}
                  <div
                    className={classes.Link}
                    onClick={handleLogout}
                    onKeyPress={handleLogout}
                    role="button"
                  >
                    <MenuItem>
                      <img
                        alt="ico-batail"
                        src={`${publicIconPath}/deconnexion.svg`}
                        className={classes.iconImgLogout}
                      />
                      <Text>Déconnexion</Text>
                    </MenuItem>
                  </div>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </span>
  );
}
